/**
 * Comprehensive Client Seed Data
 * TASK-210: Complete frontend seed data for 3 clients with full profiles
 *
 * Contains:
 * - Basic client data
 * - KYC profiles
 * - Risk profiles (10-section investment questionnaire)
 * - Metadata (meeting notes, life events, communications, extractions)
 * - Portfolio data (separate file)
 * - Document references
 */

import { Client } from '../clientService';

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

// KYC Profile structure matching the API format that KYCTab expects
export interface KYCProfile {
  client_id: string;

  // Personal Info (flattened structure for API)
  first_name: string;
  middle_names?: string;
  last_name: string;
  passport_number?: string;
  national_id?: string;
  ssn?: string;
  date_of_birth: string;
  place_of_birth?: string;
  gender?: 'male' | 'female' | 'other' | 'prefer_not_to_say';
  marital_status?: 'single' | 'married' | 'divorced' | 'widowed' | 'separated' | 'domestic_partnership';
  nationalities: string[]; // ISO codes
  former_names?: string[];
  languages?: string[];

  // Contact Info
  phone_primary: string;
  phone_secondary?: string;
  email_primary: string;
  email_secondary?: string;
  preferred_contact_method?: 'email' | 'phone' | 'whatsapp' | 'video_call' | 'in_person';
  relationship_reason?: string;
  relationship_purpose?: string;

  // Addresses
  residential_address: {
    street_address: string;
    street_address_2?: string;
    city: string;
    state_province?: string;
    postal_code: string;
    country: string;
  };
  mailing_address?: {
    street_address: string;
    street_address_2?: string;
    city: string;
    state_province?: string;
    postal_code: string;
    country: string;
  };
  previous_addresses?: any[];

  // Employment Info
  employment_status: 'employed' | 'selfEmployed' | 'unemployed' | 'retired' | 'student' | 'other';
  employer_name?: string;
  occupation?: string;
  employment_industry?: string;
  years_employed?: number;
  work_address?: {
    street_address: string;
    city: string;
    postal_code?: string;
    country: string;
  };
  annual_income?: number;
  income_currency?: string;

  // Tax Info
  tax_id?: string;
  tax_residence: string;
  us_person: boolean;
  fatca_status?: 'compliant' | 'non_compliant' | 'pending' | 'exempt';

  // Personal Background
  personal_background?: {
    education_level?: string;
    institutions_attended?: string[];
    professional_certifications?: string[];
    hobbies_interests?: string[];
    family_composition?: string;
    dependents_count?: number;
    spouse_partner_employment_status?: string;
  };

  // PEP Status
  pep_status?: {
    classification: 'not_pep' | 'pep' | 'rca';
    is_pep: boolean;
    is_rca: boolean;
    pep_type?: 'domestic' | 'foreign' | 'international';
    positions: string[];
    countries: string[];
    board_position_isins?: string[];
    start_date?: string;
    end_date?: string;
    years_in_position?: number;
    years_since_exit?: number;
    related_peps?: Array<{
      name: string;
      relationship: string;
    }>;
    relationship_type?: string;
    pep_risk_level?: 'low' | 'medium' | 'high' | 'critical';
    enhanced_due_diligence?: boolean;
    monitoring_frequency?: string;
  };

  // Adverse Media
  adverse_media?: Array<{
    screening_date: string;
    screening_tool: string;
    has_adverse_media: boolean;
    severity_level?: 'low' | 'medium' | 'high' | 'critical';
    findings?: Array<{
      type: string;
      summary: string;
      categories: string[];
    }>;
    categories?: string[];
    reviewed: boolean;
    reviewed_by: string;
    false_positive: boolean;
  }>;

  // Business Activities
  business_activities?: Array<{
    business_name: string;
    business_type: string;
    registration_number?: string;
    registration_country?: string;
    primary_industry: string;
    ownership_percentage?: number;
    annual_revenue?: number;
    revenue_currency?: string;
  }>;

  // Wealth Sources
  wealth_sources?: Array<{
    source_type: string;
    source_description: string;
    percentage?: number;
    asset_type?: string;
    asset_value?: number;
    asset_currency?: string;
    asset_location?: string;
    documented?: boolean;
  }>;

  // Wealth Profile-Level Fields
  wealth_narrative?: string;
  total_net_worth?: number;
  liquid_net_worth?: number;
  net_worth_currency?: string;
  primary_wealth_source?: string;

  // Income Sources
  income_sources?: Array<{
    income_type: string;
    income_description: string;
    annual_amount: number;
    currency: string;
    source_name?: string;
    is_ongoing?: boolean;
  }>;

  // Metadata
  created_at?: string;
  updated_at?: string;
  status?: 'draft' | 'pending' | 'in_review' | 'approved' | 'rejected';
  version?: number;
}

export interface RiskProfile {
  status: 'not_started' | 'in_progress' | 'completed';
  completed_date?: string;
  last_updated?: string;

  section1?: {
    years_investing: string;
    asset_classes_familiar: string[];
    professional_experience: boolean;
    certifications?: string[];
  };

  section2?: {
    primary_objective: string;
    secondary_objective: string;
    time_horizon: string;
    liquidity_needs: string;
    planned_withdrawals?: string;
  };

  section3?: {
    annual_income: string;
    liquid_assets: string;
    illiquid_assets: string;
    liabilities: string;
    net_worth: string;
    emergency_fund: string;
  };

  section4?: {
    comfort_with_volatility: string;
    max_acceptable_loss_1year: string;
    reaction_to_market_downturn: string;
    sleep_at_night_factor: string;
    past_investment_losses?: string;
  };

  section5?: {
    dependency_on_portfolio: string;
    other_income_sources: string[];
    insurance_coverage: string;
    health_status?: string;
    dependents: number;
  };

  section6?: {
    risk_profile_category: 'conservative' | 'moderate' | 'aggressive' | 'very_aggressive';
    target_allocation: {
      equities: string;
      fixed_income: string;
      alternatives: string;
      cash: string;
    };
    expected_return: string;
    expected_volatility: string;
    determined_by: string;
    determined_date: string;
  };

  section7?: {
    ethical_restrictions: string[];
    geographic_preferences: string;
    currency_preferences: string;
    leverage_acceptance: boolean;
    derivatives_acceptance: string;
  };

  section8?: {
    tax_optimization_priority: string;
    tax_loss_harvesting: boolean;
    dividend_vs_capital_gains: string;
    cross_border_considerations: string;
  };

  section9?: {
    beneficiaries: string[];
    inheritance_strategy: string;
    charitable_intentions: string;
    succession_plan: string;
  };

  section10?: {
    accredited_investor: boolean;
    professional_investor: boolean;
    appropriateness_test_passed: boolean;
    mifid_classification: string;
    advisory_agreement_signed: boolean;
    advisory_agreement_date: string;
  };
}

export interface ClientMetadata {
  meeting_notes: Array<{
    id: string;
    date: string;
    type: string;
    advisor: string;
    duration_minutes: number;
    summary: string;
    key_points: string[];
    action_items?: string[];
  }>;

  life_events: Array<{
    id: string;
    date: string;
    type: string;
    description: string;
    financial_impact: string;
    portfolio_impact: string;
  }>;

  communications: Array<{
    id: string;
    date: string;
    type: 'email' | 'phone' | 'meeting' | 'video_call';
    direction: 'inbound' | 'outbound';
    subject: string;
    duration_minutes?: number;
    summary: string;
  }>;

  extraction_history: Array<{
    id: string;
    upload_id: string;
    filename: string;
    extraction_type: string;
    status: string;
    created_at: string;
    completed_at?: string;
    processing_time_ms?: number;
    extracted_data?: any;
  }>;
}

export interface ExtendedClient extends Client {
  date_of_birth?: string;
  nationality?: string;
  tax_residency?: string;
  occupation?: string;
  marital_status?: string;
  address?: {
    street: string;
    city: string;
    postal_code: string;
    country: string;
  };
  // Note: KYC profiles are stored separately in SEED_KYC_PROFILES registry
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

// Helper functions removed - unused in this file
// Total AUM is calculated by getTotalAuMForClient from portfolioMockData

// ============================================================================
// CLIENT 1: JOHANN MUELLER
// Conservative, High Net Worth Individual, Retired CFO
// ============================================================================

export const JOHANN_MUELLER_CLIENT: ExtendedClient = {
  id: 'CUST-001-ABC123',
  name: 'Johann Mueller',
  email: 'johann.mueller@email.com',
  phone: '+41 79 123 4567',
  type: 'Individual',
  jurisdiction: 'Switzerland',
  status: 'client',
  is_active: true,
  onboarding_completed_at: '2024-06-15T10:00:00Z',
  created_at: '2024-06-01T09:30:00Z',
  updated_at: '2025-01-12T14:20:00Z',
  total_documents: 15,
  last_upload_date: '2025-01-05T11:30:00Z',

  // Extended fields
  date_of_birth: '1965-03-15',
  nationality: 'Swiss',
  tax_residency: 'Switzerland',
  occupation: 'Retired Executive',
  marital_status: 'Married',
  address: {
    street: 'Seestrasse 125',
    city: 'Zurich',
    postal_code: '8001',
    country: 'Switzerland'
  },

  // Risk Profile (10 sections)
  risk_profile: {
    status: 'completed',
    completed_date: '2024-06-20',
    last_updated: '2024-11-15',

    section1: {
      full_name: 'Johann Mueller',
      date_of_birth: '1965-03-15',
      years_investing: '20+',
      asset_classes_familiar: ['equities', 'bonds', 'real_estate', 'alternative_investments'],
      professional_experience: true,
      certifications: ['CFA Level II'],
      employment: {
        occupation: 'Retired CFO',
        employer: 'Roche Pharmaceuticals (Retired)',
        years_at_current: 'N/A'
      },
      family: {
        marital_status: 'Married',
        dependents: 1,
        spouse_employment: 'Retired'
      }
    },

    section2: {
      primary_objective: 'wealth_preservation',
      secondary_objective: 'income_generation',
      time_horizon: '10-15 years',
      liquidity_needs: 'low',
      planned_withdrawals: 'CHF 150,000 annually for living expenses'
    },

    section3: {
      annual_income: 'CHF 180,000 (pension + rental income)',
      liquid_assets: 'CHF 3.2 Million (securities + cash)',
      illiquid_assets: 'CHF 5.0 Million (real estate + art)',
      liabilities_summary: 'CHF 200,000 (mortgage)',
      net_worth: 'CHF 8.0 Million',
      emergency_fund: 'CHF 250,000 (6 months expenses)',
      // Detailed financial breakdown for snapshot - matches portfolio totals
      // Johann has: UBS Main (~1.8M securities), CS Pension (~900K securities), Private Assets (1.515M alternative)
      assets: {
        total_assets: 8200000,
        cash_deposits: 280000,
        securities_investments: 2700000, // PORT-001-UBS + PORT-002-CS securities
        real_estate_market_value: 3500000,
        business_interests: 0,
        other_assets: 1720000 // Art collection + other valuables
      },
      liabilities: {
        total_liabilities: 200000,
        mortgage: 200000,
        other_debt: 0
      },
      income: {
        total_annual_net: 180000,
        employment_income: 0,
        pension_income: 120000,
        investment_income: 45000,
        rental_income: 15000
      },
      max_loss_capacity: 'Low (10%)'
    },

    section4: {
      comfort_with_volatility: 'moderate',
      max_acceptable_loss_1year: '10%',
      reaction_to_market_downturn: 'hold_and_rebalance',
      sleep_at_night_factor: 'conservative',
      past_investment_losses: 'Experienced 2008 crisis, learned to diversify',
      // Goals and time horizon
      purposes: ['Wealth Preservation', 'Income Generation', 'Legacy Planning'],
      time_horizon: '10-15 years',
      liquidity: {
        regular_withdrawals: true,
        withdrawal_frequency: 'Annually',
        withdrawal_amount: 'CHF 150,000'
      }
    },

    section5: {
      dependency_on_portfolio: 'moderate',
      other_income_sources: ['pension', 'rental_income'],
      insurance_coverage: 'comprehensive',
      health_status: 'excellent',
      dependents: 1,
      // Risk attitude
      risk_attitude: 'Conservative',
      // Constraints
      restrictions: {
        esg_criteria: true,
        esg_specify: 'Environmental focus',
        sector_exclusions: true,
        sectors_exclude: ['Tobacco', 'Weapons', 'Fossil Fuels'],
        geographic_restrictions: false,
        currency_restrictions: false
      }
    },

    section6: {
      risk_profile_category: 'conservative',
      target_allocation: {
        equities: '45%',
        fixed_income: '35%',
        alternatives: '10%',
        cash: '10%'
      },
      expected_return: '5-7% annually',
      expected_volatility: '8-12%',
      determined_by: 'advisor',
      determined_date: '2024-06-20'
    },

    section7: {
      ethical_restrictions: ['no tobacco', 'no weapons'],
      geographic_preferences: 'Switzerland-focused, some global diversification',
      currency_preferences: 'CHF base, accept USD/EUR exposure up to 30%',
      leverage_acceptance: false,
      derivatives_acceptance: 'for hedging only'
    },

    section8: {
      tax_optimization_priority: 'high',
      tax_loss_harvesting: true,
      dividend_vs_capital_gains: 'balanced',
      cross_border_considerations: 'None - Swiss resident only'
    },

    section9: {
      beneficiaries: ['spouse', 'children'],
      inheritance_strategy: 'preserve_and_grow',
      charitable_intentions: 'Yes - 5% to environmental causes',
      succession_plan: 'documented'
    },

    section10: {
      accredited_investor: true,
      professional_investor: true,
      appropriateness_test_passed: true,
      mifid_classification: 'elective_professional',
      advisory_agreement_signed: true,
      advisory_agreement_date: '2024-06-15'
    }
  },

  // Metadata
  metadata: {
    meeting_notes: [
      {
        id: 'NOTE-JM-001',
        date: '2024-06-20',
        type: 'initial_consultation',
        advisor: 'Sarah Meyer',
        duration_minutes: 90,
        summary: 'Initial consultation to discuss investment objectives and risk tolerance. Client seeks capital preservation with moderate growth.',
        key_points: [
          'Retiring from CFO position at Roche',
          'Looking to preserve wealth for retirement',
          'Interested in sustainable investments',
          'Willing to accept moderate volatility for higher returns'
        ],
        action_items: [
          'Draft investment policy statement',
          'Review existing holdings at UBS',
          'Propose portfolio allocation strategy'
        ]
      },
      {
        id: 'NOTE-JM-002',
        date: '2024-09-15',
        type: 'portfolio_review',
        advisor: 'Sarah Meyer',
        duration_minutes: 60,
        summary: 'Q3 portfolio review. Performance on track with expectations. Discussed minor rebalancing needs.',
        key_points: [
          'Portfolio up 5.2% YTD',
          'Equity allocation slightly above target',
          'Client satisfied with progress',
          'No major concerns'
        ]
      },
      {
        id: 'NOTE-JM-003',
        date: '2024-12-10',
        type: 'annual_review',
        advisor: 'Sarah Meyer',
        duration_minutes: 120,
        summary: 'Comprehensive annual portfolio and financial planning review. Updated risk profile and discussed estate planning.',
        key_points: [
          'Annual return 8.3% vs target 6%',
          'Exceeded performance expectations',
          'Discussed estate planning updates',
          'Added real estate investment to portfolio tracking'
        ],
        action_items: [
          'Update estate planning documents',
          'Schedule meeting with tax advisor',
          'Review insurance coverage'
        ]
      }
    ],

    life_events: [
      {
        id: 'EVENT-JM-001',
        date: '2024-06-01',
        type: 'retirement',
        description: 'Retired from CFO position at Roche Pharmaceuticals after 18 years',
        financial_impact: 'Received retirement package of CHF 2.1M',
        portfolio_impact: 'Increased liquidity, repositioned portfolio for income generation'
      },
      {
        id: 'EVENT-JM-002',
        date: '2024-09-10',
        type: 'property_acquisition',
        description: 'Purchased luxury apartment in Zurich Seefeld district',
        financial_impact: 'CHF 1.45M investment in real estate',
        portfolio_impact: 'Diversified into alternative assets, added to private assets portfolio'
      }
    ],

    communications: [
      {
        id: 'COMM-JM-001',
        date: '2024-07-05',
        type: 'email',
        direction: 'outbound',
        subject: 'Q2 Portfolio Statement',
        summary: 'Sent quarterly portfolio statement and performance report'
      },
      {
        id: 'COMM-JM-002',
        date: '2024-10-15',
        type: 'phone',
        direction: 'inbound',
        subject: 'Question about tax optimization',
        duration_minutes: 15,
        summary: 'Client inquired about tax-loss harvesting opportunities for year-end planning'
      },
      {
        id: 'COMM-JM-003',
        date: '2025-01-05',
        type: 'email',
        direction: 'inbound',
        subject: 'Document upload - Picasso valuation',
        summary: 'Client uploaded art valuation certificate for portfolio tracking purposes'
      }
    ],

    extraction_history: [
      {
        id: 'EXT-JM-001',
        upload_id: 'UP-JM-001',
        filename: 'passport_scan.pdf',
        extraction_type: 'identity_document',
        status: 'completed',
        created_at: '2024-06-02T10:15:00Z',
        completed_at: '2024-06-02T10:16:23Z',
        processing_time_ms: 83000,
        extracted_data: {
          document_type: 'passport',
          name: 'Johann Mueller',
          document_number: 'P1234567',
          nationality: 'Swiss'
        }
      },
      {
        id: 'EXT-JM-002',
        upload_id: 'UP-JM-008',
        filename: 'ubs_statement_2024Q3.pdf',
        extraction_type: 'bank_statement',
        status: 'completed',
        created_at: '2024-10-05T14:20:00Z',
        completed_at: '2024-10-05T14:21:45Z',
        processing_time_ms: 105000,
        extracted_data: {
          account_balance: 1825000,
          positions_count: 7,
          transactions_count: 12
        }
      }
    ]
  }
};

export const JOHANN_MUELLER_KYC: KYCProfile = {
  client_id: 'CUST-001-ABC123',

  // Personal Info
  first_name: 'Johann',
  middle_names: undefined,
  last_name: 'Mueller',
  passport_number: 'P1234567',
  national_id: undefined,
  ssn: undefined,
  date_of_birth: '1965-03-15',
  place_of_birth: 'Zurich, Switzerland',
  gender: 'male',
  marital_status: 'married',
  nationalities: ['CH'],
  former_names: [],
  languages: ['de', 'en', 'fr'],

  // Contact Info
  phone_primary: '+41 79 123 4567',
  phone_secondary: undefined,
  email_primary: 'johann.mueller@email.com',
  email_secondary: undefined,
  preferred_contact_method: 'email',
  relationship_reason: 'Wealth management and retirement planning',
  relationship_purpose: 'Long-term investment and capital preservation',

  // Addresses
  residential_address: {
    street_address: 'Seestrasse 125',
    street_address_2: undefined,
    city: 'Zurich',
    state_province: undefined,
    postal_code: '8001',
    country: 'CH'
  },
  mailing_address: undefined,
  previous_addresses: [],

  // Employment Info
  employment_status: 'retired',
  employer_name: 'Roche Pharmaceuticals (Retired)',
  occupation: 'Former Chief Financial Officer',
  employment_industry: 'Pharmaceuticals',
  years_employed: 19,
  work_address: undefined,
  annual_income: 180000,
  income_currency: 'CHF',

  // Tax Info
  tax_id: undefined,
  tax_residence: 'CH',
  us_person: false,
  fatca_status: 'exempt',

  // Personal Background
  personal_background: {
    education_level: 'Master of Business Administration',
    institutions_attended: ['University of Zurich', 'INSEAD'],
    professional_certifications: ['CFA Level II'],
    hobbies_interests: ['Sailing', 'Art Collection', 'Golf'],
    family_composition: 'Married with 2 adult children',
    dependents_count: 1,
    spouse_partner_employment_status: 'Retired'
  },

  // PEP Status
  pep_status: {
    classification: 'not_pep',
    is_pep: false,
    is_rca: false,
    pep_type: undefined,
    positions: [],
    countries: [],
    board_position_isins: [],
    start_date: undefined,
    end_date: undefined,
    years_in_position: undefined,
    years_since_exit: undefined,
    related_peps: [],
    relationship_type: undefined,
    pep_risk_level: 'low',
    enhanced_due_diligence: false,
    monitoring_frequency: 'annual'
  },

  // Adverse Media
  adverse_media: [{
    screening_date: '2025-01-10',
    screening_tool: 'WorldCheck',
    has_adverse_media: false,
    severity_level: undefined,
    findings: [],
    categories: [],
    reviewed: true,
    reviewed_by: 'compliance_team',
    false_positive: false
  }],

  // Business Activities
  business_activities: [],

  // Wealth Sources
  wealth_sources: [
    {
      source_type: 'employment_income',
      source_description: 'Executive compensation and stock options from Roche Pharmaceuticals (2005-2024)',
      percentage: 70,
      asset_type: 'securities',
      asset_value: 5600000,
      asset_currency: 'CHF',
      asset_location: 'Switzerland',
      documented: true
    },
    {
      source_type: 'pension',
      source_description: 'Retirement package and pension benefits',
      percentage: 20,
      asset_type: 'pension_fund',
      asset_value: 1600000,
      asset_currency: 'CHF',
      asset_location: 'Switzerland',
      documented: true
    },
    {
      source_type: 'real_estate',
      source_description: 'Property investments',
      percentage: 10,
      asset_type: 'real_estate',
      asset_value: 800000,
      asset_currency: 'CHF',
      asset_location: 'Switzerland',
      documented: true
    }
  ],

  // Wealth Profile-Level
  wealth_narrative: 'Wealth accumulated through successful 19-year career as CFO of Roche Pharmaceuticals. Received executive compensation, stock options, and comprehensive retirement package. Conservative investor with focus on preservation.',
  total_net_worth: 8000000,
  liquid_net_worth: 3200000,
  net_worth_currency: 'CHF',
  primary_wealth_source: 'Employment Income',

  // Income Sources
  income_sources: [
    {
      income_type: 'pension',
      income_description: 'Roche pension plan',
      annual_amount: 120000,
      currency: 'CHF',
      source_name: 'Roche Pharmaceuticals Pension Fund',
      is_ongoing: true
    },
    {
      income_type: 'investment_income',
      income_description: 'Dividends and interest',
      annual_amount: 45000,
      currency: 'CHF',
      source_name: 'Investment Portfolio',
      is_ongoing: true
    },
    {
      income_type: 'rental_income',
      income_description: 'Rental property income',
      annual_amount: 15000,
      currency: 'CHF',
      source_name: 'Real Estate Holdings',
      is_ongoing: true
    }
  ],

  // Metadata
  created_at: '2024-06-01T09:30:00Z',
  updated_at: '2025-01-12T14:20:00Z',
  status: 'approved',
  version: 1
};

// ============================================================================
// CLIENT 2: MARIA SCHMIDT
// Moderate Risk, Growth-Oriented, Tech Entrepreneur
// ============================================================================

export const MARIA_SCHMIDT_CLIENT: ExtendedClient = {
  id: 'CUST-002-DEF456',
  name: 'Maria Schmidt',
  email: 'maria.schmidt@techventures.ch',
  phone: '+41 78 234 5678',
  type: 'Individual',
  jurisdiction: 'Switzerland',
  status: 'client',
  is_active: true,
  onboarding_completed_at: '2024-05-10T14:30:00Z',
  created_at: '2024-04-20T11:15:00Z',
  updated_at: '2025-01-11T16:45:00Z',
  total_documents: 12,
  last_upload_date: '2024-12-20T09:20:00Z',

  date_of_birth: '1978-08-22',
  nationality: 'Swiss',
  tax_residency: 'Switzerland',
  occupation: 'Tech Entrepreneur / CEO',
  marital_status: 'Single',
  address: {
    street: 'Bahnhofstrasse 45',
    city: 'Zurich',
    postal_code: '8001',
    country: 'Switzerland'
  },

  risk_profile: {
    status: 'completed',
    completed_date: '2024-05-15',
    last_updated: '2024-10-20',

    section1: {
      full_name: 'Maria Schmidt',
      date_of_birth: '1978-08-22',
      years_investing: '10-15',
      asset_classes_familiar: ['equities', 'venture_capital', 'cryptocurrency', 'startup_equity'],
      professional_experience: true,
      certifications: [],
      employment: {
        occupation: 'Tech Entrepreneur / CEO',
        employer: 'NextGen AI Solutions',
        years_at_current: '2 years'
      },
      family: {
        marital_status: 'Single',
        dependents: 0,
        spouse_employment: 'N/A'
      }
    },

    section2: {
      primary_objective: 'capital_growth',
      secondary_objective: 'wealth_accumulation',
      time_horizon: '15-20 years',
      liquidity_needs: 'medium',
      planned_withdrawals: 'None planned, reinvesting for growth'
    },

    section3: {
      annual_income: 'CHF 350,000 (salary + dividends)',
      liquid_assets: 'CHF 3.5 Million (securities + cash)',
      illiquid_assets: 'CHF 5.1 Million (startup equity + real estate + crypto)',
      liabilities_summary: 'CHF 450,000 (mortgage)',
      net_worth: 'CHF 8.15 Million',
      emergency_fund: 'CHF 180,000 (1 year expenses)',
      // Detailed financial breakdown - matches portfolio totals
      // Maria has: Julius Baer (~1.8M securities), Vontobel Growth (~1.2M securities + 195K crypto)
      assets: {
        total_assets: 8600000,
        cash_deposits: 320000,
        securities_investments: 3000000, // PORT-004-JB + PORT-005-VONTOBEL securities
        real_estate_market_value: 2200000,
        business_interests: 2500000, // Startup equity
        other_assets: 580000 // Crypto (195K) + other valuables
      },
      liabilities: {
        total_liabilities: 450000,
        mortgage: 450000,
        other_debt: 0
      },
      income: {
        total_annual_net: 350000,
        employment_income: 180000,
        pension_income: 0,
        investment_income: 95000,
        business_income: 75000
      },
      max_loss_capacity: 'High (20%)'
    },

    section4: {
      comfort_with_volatility: 'high',
      max_acceptable_loss_1year: '20%',
      reaction_to_market_downturn: 'buy_more',
      sleep_at_night_factor: 'aggressive',
      past_investment_losses: 'Lost 30% in tech bubble 2022, stayed invested and recovered',
      // Goals and time horizon
      purposes: ['Capital Growth', 'Wealth Accumulation', 'Financial Independence'],
      time_horizon: '15-20 years',
      liquidity: {
        regular_withdrawals: false,
        withdrawal_frequency: 'As needed',
        withdrawal_amount: 'None planned'
      }
    },

    section5: {
      dependency_on_portfolio: 'low',
      other_income_sources: ['business_income', 'consulting'],
      insurance_coverage: 'adequate',
      health_status: 'excellent',
      dependents: 0,
      // Risk attitude
      risk_attitude: 'Aggressive',
      // Constraints
      restrictions: {
        esg_criteria: true,
        esg_specify: 'Sustainable tech focus',
        sector_exclusions: false,
        sectors_exclude: [],
        geographic_restrictions: false,
        currency_restrictions: false
      }
    },

    section6: {
      risk_profile_category: 'aggressive',
      target_allocation: {
        equities: '70%',
        fixed_income: '15%',
        alternatives: '10%',
        cash: '5%'
      },
      expected_return: '10-15% annually',
      expected_volatility: '15-20%',
      determined_by: 'advisor',
      determined_date: '2024-05-15'
    },

    section7: {
      ethical_restrictions: ['ESG-focused', 'sustainable_tech'],
      geographic_preferences: 'Global diversification, tech-heavy allocation',
      currency_preferences: 'Multi-currency, comfortable with USD/EUR exposure',
      leverage_acceptance: true,
      derivatives_acceptance: 'yes - options for growth'
    },

    section8: {
      tax_optimization_priority: 'medium',
      tax_loss_harvesting: true,
      dividend_vs_capital_gains: 'prefer_capital_gains',
      cross_border_considerations: 'Potential US expansion, monitor FATCA compliance'
    },

    section9: {
      beneficiaries: ['parents', 'siblings'],
      inheritance_strategy: 'growth_and_impact',
      charitable_intentions: 'Yes - tech education for underprivileged youth',
      succession_plan: 'in_progress'
    },

    section10: {
      accredited_investor: true,
      professional_investor: true,
      appropriateness_test_passed: true,
      mifid_classification: 'professional',
      advisory_agreement_signed: true,
      advisory_agreement_date: '2024-05-10'
    }
  },

  metadata: {
    meeting_notes: [
      {
        id: 'NOTE-MS-001',
        date: '2024-05-10',
        type: 'initial_consultation',
        advisor: 'Thomas Weber',
        duration_minutes: 75,
        summary: 'Successful tech entrepreneur seeking sophisticated portfolio management. High risk tolerance, growth-focused.',
        key_points: [
          'Sold startup in 2023 for significant exit',
          'Looking for aggressive growth strategies',
          'Interested in sustainable investing',
          'Comfortable with high volatility'
        ],
        action_items: [
          'Set up growth portfolio with tech exposure',
          'Research ESG investment opportunities',
          'Consider venture capital allocations'
        ]
      },
      {
        id: 'NOTE-MS-002',
        date: '2024-08-20',
        type: 'portfolio_review',
        advisor: 'Thomas Weber',
        duration_minutes: 45,
        summary: 'Mid-year review. Portfolio performing exceptionally well. Client wants to increase tech exposure.',
        key_points: [
          'YTD return 14.2%',
          'Outperforming benchmark significantly',
          'Request to add more US tech stocks',
          'Considering cryptocurrency allocation'
        ]
      },
      {
        id: 'NOTE-MS-003',
        date: '2024-11-15',
        type: 'strategy_meeting',
        advisor: 'Thomas Weber',
        duration_minutes: 90,
        summary: 'Strategic planning for 2025. Discussed new business venture and portfolio implications.',
        key_points: [
          'Planning to start new AI venture',
          'May need liquidity in Q2 2025',
          'Wants to maintain aggressive growth',
          'Interested in alternative investments'
        ]
      }
    ],

    life_events: [
      {
        id: 'EVENT-MS-001',
        date: '2023-09-15',
        type: 'business_exit',
        description: 'Sold tech startup to multinational corporation',
        financial_impact: 'Received CHF 6.8M from acquisition',
        portfolio_impact: 'Initial investment capital, established investment portfolio'
      },
      {
        id: 'EVENT-MS-002',
        date: '2024-07-01',
        type: 'property_purchase',
        description: 'Purchased penthouse in Zurich city center',
        financial_impact: 'CHF 2.2M investment',
        portfolio_impact: 'Minor - used separate funds'
      }
    ],

    communications: [
      {
        id: 'COMM-MS-001',
        date: '2024-06-10',
        type: 'email',
        direction: 'outbound',
        subject: 'Q2 Performance Report',
        summary: 'Quarterly report showing strong performance'
      },
      {
        id: 'COMM-MS-002',
        date: '2024-09-05',
        type: 'video_call',
        direction: 'inbound',
        subject: 'Portfolio rebalancing discussion',
        duration_minutes: 30,
        summary: 'Client initiated discussion on increasing equity exposure'
      },
      {
        id: 'COMM-MS-003',
        date: '2024-12-20',
        type: 'email',
        direction: 'inbound',
        subject: 'Year-end tax planning',
        summary: 'Client requesting tax-loss harvesting opportunities'
      }
    ],

    extraction_history: [
      {
        id: 'EXT-MS-001',
        upload_id: 'UP-MS-001',
        filename: 'passport_maria_schmidt.pdf',
        extraction_type: 'identity_document',
        status: 'completed',
        created_at: '2024-04-22T11:30:00Z',
        completed_at: '2024-04-22T11:31:15Z',
        processing_time_ms: 75000
      },
      {
        id: 'EXT-MS-002',
        upload_id: 'UP-MS-005',
        filename: 'julius_baer_statement_q2.pdf',
        extraction_type: 'bank_statement',
        status: 'completed',
        created_at: '2024-07-15T09:45:00Z',
        completed_at: '2024-07-15T09:46:30Z',
        processing_time_ms: 90000
      }
    ]
  }
};

export const MARIA_SCHMIDT_KYC: KYCProfile = {
  client_id: 'CUST-002-DEF456',

  // Personal Info
  first_name: 'Maria',
  middle_names: undefined,
  last_name: 'Schmidt',
  passport_number: 'P7654321',
  national_id: undefined,
  ssn: undefined,
  date_of_birth: '1978-08-22',
  place_of_birth: 'Basel, Switzerland',
  gender: 'female',
  marital_status: 'single',
  nationalities: ['CH'],
  former_names: [],
  languages: ['de', 'en'],

  // Contact Info
  phone_primary: '+41 78 234 5678',
  phone_secondary: undefined,
  email_primary: 'maria.schmidt@techventures.ch',
  email_secondary: undefined,
  preferred_contact_method: 'email',
  relationship_reason: 'Growth-focused wealth management',
  relationship_purpose: 'Capital appreciation and portfolio diversification',

  // Addresses
  residential_address: {
    street_address: 'Bahnhofstrasse 45',
    street_address_2: undefined,
    city: 'Zurich',
    state_province: undefined,
    postal_code: '8001',
    country: 'CH'
  },
  mailing_address: undefined,
  previous_addresses: [],

  // Employment Info
  employment_status: 'selfEmployed',
  employer_name: 'NextGen AI Solutions',
  occupation: 'CEO & Founder',
  employment_industry: 'Technology / Artificial Intelligence',
  years_employed: 2,
  work_address: {
    street_address: 'Technoparkstrasse 1',
    city: 'Zurich',
    postal_code: '8005',
    country: 'CH'
  },
  annual_income: 350000,
  income_currency: 'CHF',

  // Tax Info
  tax_id: undefined,
  tax_residence: 'CH',
  us_person: false,
  fatca_status: 'exempt',

  // Personal Background
  personal_background: {
    education_level: 'Master of Science in Computer Science',
    institutions_attended: ['ETH Zurich', 'Stanford University'],
    professional_certifications: [],
    hobbies_interests: ['Technology', 'Startups', 'Yoga', 'Travel'],
    family_composition: 'Single',
    dependents_count: 0,
    spouse_partner_employment_status: undefined
  },

  // PEP Status
  pep_status: {
    classification: 'not_pep',
    is_pep: false,
    is_rca: false,
    pep_type: undefined,
    positions: [],
    countries: [],
    board_position_isins: [],
    start_date: undefined,
    end_date: undefined,
    years_in_position: undefined,
    years_since_exit: undefined,
    related_peps: [],
    relationship_type: undefined,
    pep_risk_level: 'low',
    enhanced_due_diligence: false,
    monitoring_frequency: 'annual'
  },

  // Adverse Media
  adverse_media: [{
    screening_date: '2025-01-05',
    screening_tool: 'WorldCheck',
    has_adverse_media: false,
    severity_level: undefined,
    findings: [],
    categories: [],
    reviewed: true,
    reviewed_by: 'compliance_team',
    false_positive: false
  }],

  // Business Activities
  business_activities: [
    {
      business_name: 'NextGen AI Solutions',
      business_type: 'private_limited_company',
      registration_number: 'CHE-987.654.321',
      registration_country: 'CH',
      primary_industry: 'Artificial Intelligence',
      ownership_percentage: 60,
      annual_revenue: 1200000,
      revenue_currency: 'CHF'
    }
  ],

  // Wealth Sources
  wealth_sources: [
    {
      source_type: 'business_sale',
      source_description: 'Sale of DataFlow Analytics AG to multinational corporation (2023)',
      percentage: 80,
      asset_type: 'cash_and_securities',
      asset_value: 6800000,
      asset_currency: 'CHF',
      asset_location: 'Switzerland',
      documented: true
    },
    {
      source_type: 'business_equity',
      source_description: 'Ownership stake in NextGen AI Solutions',
      percentage: 15,
      asset_type: 'business_equity',
      asset_value: 1500000,
      asset_currency: 'CHF',
      asset_location: 'Switzerland',
      documented: true
    },
    {
      source_type: 'real_estate',
      source_description: 'Penthouse in Zurich city center',
      percentage: 5,
      asset_type: 'real_estate',
      asset_value: 400000,
      asset_currency: 'CHF',
      asset_location: 'Switzerland',
      documented: true
    }
  ],

  // Wealth Profile-Level
  wealth_narrative: 'Successful tech entrepreneur. Founded and scaled DataFlow Analytics AG, sold to multinational corporation for CHF 6.8M in 2023. Currently building second venture in AI space. High risk tolerance with growth focus.',
  total_net_worth: 8150000,
  liquid_net_worth: 3500000,
  net_worth_currency: 'CHF',
  primary_wealth_source: 'Business Sale',

  // Income Sources
  income_sources: [
    {
      income_type: 'employment_income',
      income_description: 'CEO salary from NextGen AI Solutions',
      annual_amount: 180000,
      currency: 'CHF',
      source_name: 'NextGen AI Solutions',
      is_ongoing: true
    },
    {
      income_type: 'investment_income',
      income_description: 'Dividends and capital gains',
      annual_amount: 95000,
      currency: 'CHF',
      source_name: 'Investment Portfolio',
      is_ongoing: true
    },
    {
      income_type: 'business_income',
      income_description: 'Dividends from NextGen AI Solutions',
      annual_amount: 75000,
      currency: 'CHF',
      source_name: 'NextGen AI Solutions',
      is_ongoing: true
    }
  ],

  // Metadata
  created_at: '2024-04-20T11:15:00Z',
  updated_at: '2025-01-11T16:45:00Z',
  status: 'approved',
  version: 1
};

// ============================================================================
// CLIENT 3: ACME CORPORATION AG
// Aggressive, Corporate Treasury, Multi-Portfolio
// ============================================================================

export const ACME_CORPORATION_CLIENT: ExtendedClient = {
  id: 'CUST-003-GHI789',
  name: 'Acme Corporation AG',
  email: 'treasury@acmecorp.ch',
  phone: '+41 44 123 4567',
  type: 'Corporate',
  jurisdiction: 'Switzerland',
  status: 'client',
  is_active: true,
  onboarding_completed_at: '2024-03-20T10:00:00Z',
  created_at: '2024-02-15T14:00:00Z',
  updated_at: '2025-01-12T10:30:00Z',
  total_documents: 22,
  last_upload_date: '2025-01-08T15:45:00Z',

  date_of_birth: undefined, // Corporate entity
  nationality: undefined,
  tax_residency: 'Switzerland',
  occupation: 'Manufacturing / Industrial',
  marital_status: undefined,
  address: {
    street: 'Industriestrasse 88',
    city: 'Basel',
    postal_code: '4056',
    country: 'Switzerland'
  },

  risk_profile: {
    status: 'completed',
    completed_date: '2024-03-25',
    last_updated: '2024-09-10',

    section1: {
      full_name: 'Acme Corporation AG',
      date_of_birth: undefined, // Corporate entity
      years_investing: '15+',
      asset_classes_familiar: ['equities', 'bonds', 'treasury_instruments', 'alternative_investments'],
      professional_experience: true,
      certifications: ['CFA', 'FRM'],
      employment: {
        occupation: 'Manufacturing Company',
        employer: 'Self (Corporation)',
        years_at_current: '27 years (founded 1998)'
      },
      family: {
        marital_status: 'N/A',
        dependents: 0,
        spouse_employment: 'N/A'
      }
    },

    section2: {
      primary_objective: 'capital_preservation',
      secondary_objective: 'liquidity_management',
      time_horizon: '5-10 years',
      liquidity_needs: 'high',
      planned_withdrawals: 'CHF 500k quarterly for operations'
    },

    section3: {
      annual_income: 'CHF 45M (revenue), CHF 8M (EBITDA)',
      liquid_assets: 'CHF 7.5 Million (securities + cash)',
      illiquid_assets: 'CHF 39.5 Million (PE funds, real estate, equipment)',
      liabilities_summary: 'CHF 15 Million (loans, bonds)',
      net_worth: 'CHF 32 Million',
      emergency_fund: 'CHF 3 Million (operating reserve)',
      // Detailed financial breakdown - matches portfolio totals
      // Acme has: Pictet Treasury (~1.2M securities), UBS Corporate (~1.8M securities), PE Holdings (23.15M PE+real estate)
      assets: {
        total_assets: 47000000,
        cash_deposits: 4500000, // High cash for treasury operations
        securities_investments: 3000000, // PORT-006-PICTET + PORT-007-UBS-CORP securities
        real_estate_market_value: 18000000, // Includes commercial property from PORT-008
        business_interests: 18000000, // PE funds + operating business equity
        other_assets: 3500000 // Equipment, inventory, other
      },
      liabilities: {
        total_liabilities: 15000000,
        mortgage: 8000000,
        business_loans: 5000000,
        other_debt: 2000000
      },
      income: {
        total_annual_net: 8000000,
        employment_income: 0,
        pension_income: 0,
        investment_income: 520000,
        business_income: 7480000
      },
      max_loss_capacity: 'Medium (15%)'
    },

    section4: {
      comfort_with_volatility: 'moderate',
      max_acceptable_loss_1year: '15%',
      reaction_to_market_downturn: 'strategic_buying',
      sleep_at_night_factor: 'balanced',
      past_investment_losses: '2020 COVID impact -8%, recovered within 6 months',
      // Goals and time horizon
      purposes: ['Capital Preservation', 'Liquidity Management', 'Treasury Growth'],
      time_horizon: '5-10 years',
      liquidity: {
        regular_withdrawals: true,
        withdrawal_frequency: 'Quarterly',
        withdrawal_amount: 'CHF 500,000 for operations'
      }
    },

    section5: {
      dependency_on_portfolio: 'low',
      other_income_sources: ['operating_revenue', 'real_estate_holdings'],
      insurance_coverage: 'comprehensive',
      dependents: 0,
      // Risk attitude
      risk_attitude: 'Moderate',
      // Constraints
      restrictions: {
        esg_criteria: true,
        esg_specify: 'Corporate ESG policy',
        sector_exclusions: true,
        sectors_exclude: ['Controversial Weapons'],
        geographic_restrictions: false,
        currency_restrictions: false
      }
    },

    section6: {
      risk_profile_category: 'moderate',
      target_allocation: {
        equities: '50%',
        fixed_income: '30%',
        alternatives: '15%',
        cash: '5%'
      },
      expected_return: '8-12% annually',
      expected_volatility: '12-18%',
      determined_by: 'board_approved',
      determined_date: '2024-03-25'
    },

    section7: {
      ethical_restrictions: ['no controversial weapons', 'ESG screening'],
      geographic_preferences: 'Global with Swiss/EU focus',
      currency_preferences: 'Multi-currency hedging strategy',
      leverage_acceptance: true,
      derivatives_acceptance: 'yes - for hedging and growth'
    },

    section8: {
      tax_optimization_priority: 'high',
      tax_loss_harvesting: true,
      dividend_vs_capital_gains: 'prefer_dividends',
      cross_border_considerations: 'EU operations, international tax planning'
    },

    section9: {
      beneficiaries: ['shareholders'],
      inheritance_strategy: 'n/a',
      charitable_intentions: 'Corporate foundation for STEM education',
      succession_plan: 'board_governed'
    },

    section10: {
      accredited_investor: true,
      professional_investor: true,
      appropriateness_test_passed: true,
      mifid_classification: 'professional_client',
      advisory_agreement_signed: true,
      advisory_agreement_date: '2024-03-20'
    }
  },

  metadata: {
    meeting_notes: [
      {
        id: 'NOTE-AC-001',
        date: '2024-03-20',
        type: 'initial_consultation',
        advisor: 'Andreas Keller',
        duration_minutes: 120,
        summary: 'Corporate treasury management mandate. Multi-portfolio structure for operating funds, growth capital, and alternatives.',
        key_points: [
          'Managing CHF 7.8M corporate treasury',
          'Need liquidity for operations',
          'Growth portfolio for excess capital',
          'Interest in alternative investments'
        ],
        action_items: [
          'Set up 3 separate portfolio mandates',
          'Establish liquidity management framework',
          'Design corporate investment policy'
        ]
      },
      {
        id: 'NOTE-AC-002',
        date: '2024-07-10',
        type: 'quarterly_review',
        advisor: 'Andreas Keller',
        duration_minutes: 90,
        summary: 'Q2 review with CFO and board treasurer. Strong performance across all portfolios.',
        key_points: [
          'Operating portfolio stable',
          'Growth portfolio up 12%',
          'Alternative investments performing well',
          'Board approved increased equity allocation'
        ]
      },
      {
        id: 'NOTE-AC-003',
        date: '2024-11-20',
        type: 'board_presentation',
        advisor: 'Andreas Keller',
        duration_minutes: 60,
        summary: 'Annual presentation to board of directors. Portfolio strategy review and 2025 outlook.',
        key_points: [
          'Total return 9.8% vs target 8%',
          'All risk metrics within tolerance',
          'Proposal to add crypto allocation',
          'Board approved alternative investments'
        ]
      }
    ],

    life_events: [
      {
        id: 'EVENT-AC-001',
        date: '2024-01-15',
        type: 'capital_raise',
        description: 'Completed CHF 5M bond issuance for expansion',
        financial_impact: 'Increased available capital for investment',
        portfolio_impact: 'Allocated to growth portfolio'
      },
      {
        id: 'EVENT-AC-002',
        date: '2024-06-01',
        type: 'acquisition',
        description: 'Acquired smaller competitor for CHF 8M',
        financial_impact: 'Used operating cash + bank financing',
        portfolio_impact: 'Temporary reduction in operating portfolio'
      },
      {
        id: 'EVENT-AC-003',
        date: '2024-10-15',
        type: 'facility_expansion',
        description: 'New production facility in Germany',
        financial_impact: 'CHF 12M capital investment',
        portfolio_impact: 'Strategic reallocation from growth portfolio'
      }
    ],

    communications: [
      {
        id: 'COMM-AC-001',
        date: '2024-04-15',
        type: 'email',
        direction: 'outbound',
        subject: 'Q1 Portfolio Performance Report',
        summary: 'Quarterly report to CFO and board treasurer'
      },
      {
        id: 'COMM-AC-002',
        date: '2024-08-20',
        type: 'meeting',
        direction: 'inbound',
        subject: 'Treasury strategy meeting',
        duration_minutes: 90,
        summary: 'Strategic discussion on cash management and investment strategy'
      },
      {
        id: 'COMM-AC-003',
        date: '2025-01-08',
        type: 'email',
        direction: 'inbound',
        subject: 'Cryptocurrency allocation approval',
        summary: 'Board approved 5% allocation to digital assets'
      }
    ],

    extraction_history: [
      {
        id: 'EXT-AC-001',
        upload_id: 'UP-AC-001',
        filename: 'company_registration_certificate.pdf',
        extraction_type: 'corporate_document',
        status: 'completed',
        created_at: '2024-02-16T10:00:00Z',
        completed_at: '2024-02-16T10:02:15Z',
        processing_time_ms: 135000
      },
      {
        id: 'EXT-AC-002',
        upload_id: 'UP-AC-012',
        filename: 'pictet_statement_q4_2024.pdf',
        extraction_type: 'bank_statement',
        status: 'completed',
        created_at: '2025-01-08T15:45:00Z',
        completed_at: '2025-01-08T15:47:20Z',
        processing_time_ms: 155000
      }
    ]
  }
};

export const ACME_CORPORATION_KYC: KYCProfile = {
  client_id: 'CUST-003-GHI789',

  // Personal Info (Corporate Entity)
  first_name: 'Acme Corporation',
  middle_names: undefined,
  last_name: 'AG',
  passport_number: undefined,
  national_id: 'CHE-123.456.789', // Commercial register number
  ssn: undefined,
  date_of_birth: '1998-05-15', // Date of incorporation
  place_of_birth: 'Basel, Switzerland', // Place of incorporation
  gender: undefined,
  marital_status: undefined,
  nationalities: ['CH'],
  former_names: [],
  languages: ['de', 'en', 'fr'],

  // Contact Info
  phone_primary: '+41 44 123 4567',
  phone_secondary: undefined,
  email_primary: 'treasury@acmecorp.ch',
  email_secondary: 'compliance@acmecorp.ch',
  preferred_contact_method: 'email',
  relationship_reason: 'Corporate treasury management and liquidity optimization',
  relationship_purpose: 'Multi-portfolio treasury management with growth and liquidity objectives',

  // Addresses
  residential_address: {
    street_address: 'Industriestrasse 88',
    street_address_2: undefined,
    city: 'Basel',
    state_province: undefined,
    postal_code: '4056',
    country: 'CH'
  },
  mailing_address: undefined,
  previous_addresses: [],

  // Employment Info (Corporate)
  employment_status: 'other',
  employer_name: undefined,
  occupation: 'Manufacturing Company',
  employment_industry: 'Industrial Manufacturing',
  years_employed: 27, // Years in business
  work_address: undefined,
  annual_income: 8000000, // EBITDA
  income_currency: 'CHF',

  // Tax Info
  tax_id: 'CHE-123.456.789',
  tax_residence: 'CH',
  us_person: false,
  fatca_status: 'exempt',

  // Personal Background (Corporate)
  personal_background: {
    education_level: 'N/A - Corporate Entity',
    institutions_attended: [],
    professional_certifications: ['ISO 9001', 'ISO 14001'],
    hobbies_interests: [],
    family_composition: 'Publicly traded corporation with distributed ownership',
    dependents_count: 0,
    spouse_partner_employment_status: undefined
  },

  // PEP Status
  pep_status: {
    classification: 'not_pep',
    is_pep: false,
    is_rca: false,
    pep_type: undefined,
    positions: [],
    countries: [],
    board_position_isins: [],
    start_date: undefined,
    end_date: undefined,
    years_in_position: undefined,
    years_since_exit: undefined,
    related_peps: [],
    relationship_type: undefined,
    pep_risk_level: 'low',
    enhanced_due_diligence: false,
    monitoring_frequency: 'annual'
  },

  // Adverse Media
  adverse_media: [{
    screening_date: '2025-01-10',
    screening_tool: 'WorldCheck Corporate',
    has_adverse_media: false,
    severity_level: undefined,
    findings: [],
    categories: [],
    reviewed: true,
    reviewed_by: 'compliance_team',
    false_positive: false
  }],

  // Business Activities
  business_activities: [
    {
      business_name: 'Acme Corporation AG',
      business_type: 'public_company',
      registration_number: 'CHE-123.456.789',
      registration_country: 'CH',
      primary_industry: 'Industrial Manufacturing',
      ownership_percentage: undefined, // Publicly traded
      annual_revenue: 45000000,
      revenue_currency: 'CHF'
    }
  ],

  // Wealth Sources
  wealth_sources: [
    {
      source_type: 'business_operations',
      source_description: 'Manufacturing and sales of industrial equipment (27 years)',
      percentage: 70,
      asset_type: 'business_equity',
      asset_value: 22400000,
      asset_currency: 'CHF',
      asset_location: 'Switzerland',
      documented: true
    },
    {
      source_type: 'real_estate',
      source_description: 'Commercial and industrial properties',
      percentage: 25,
      asset_type: 'real_estate',
      asset_value: 8000000,
      asset_currency: 'CHF',
      asset_location: 'Switzerland',
      documented: true
    },
    {
      source_type: 'capital_raising',
      source_description: 'Bond issuance for expansion (2024)',
      percentage: 5,
      asset_type: 'debt_instruments',
      asset_value: 1600000,
      asset_currency: 'CHF',
      asset_location: 'Switzerland',
      documented: true
    }
  ],

  // Wealth Profile-Level
  wealth_narrative: 'Established Swiss manufacturing company founded in 1998. Generates wealth through industrial equipment manufacturing and sales. Publicly traded on SIX Swiss Exchange with strong market presence and steady growth.',
  total_net_worth: 32000000,
  liquid_net_worth: 7500000,
  net_worth_currency: 'CHF',
  primary_wealth_source: 'Business Operations',

  // Income Sources
  income_sources: [
    {
      income_type: 'business_income',
      income_description: 'Manufacturing operations revenue',
      annual_amount: 7480000,
      currency: 'CHF',
      source_name: 'Acme Corporation AG Operations',
      is_ongoing: true
    },
    {
      income_type: 'investment_income',
      income_description: 'Investment portfolio returns',
      annual_amount: 520000,
      currency: 'CHF',
      source_name: 'Corporate Treasury Portfolio',
      is_ongoing: true
    }
  ],

  // Metadata
  created_at: '2024-02-15T14:00:00Z',
  updated_at: '2025-01-12T10:30:00Z',
  status: 'approved',
  version: 1
};

// ============================================================================
// SEED DATA REGISTRY
// ============================================================================

export const SEED_CLIENTS: ExtendedClient[] = [
  JOHANN_MUELLER_CLIENT,
  MARIA_SCHMIDT_CLIENT,
  ACME_CORPORATION_CLIENT
];

export const SEED_KYC_PROFILES: Record<string, KYCProfile> = {
  'CUST-001-ABC123': JOHANN_MUELLER_KYC,
  'CUST-002-DEF456': MARIA_SCHMIDT_KYC,
  'CUST-003-GHI789': ACME_CORPORATION_KYC
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Get client by ID
 */
export function getSeedClient(clientId: string): ExtendedClient | null {
  return SEED_CLIENTS.find(c => c.id === clientId) || null;
}

/**
 * Get all seed clients
 */
export function getAllSeedClients(): ExtendedClient[] {
  return SEED_CLIENTS;
}

/**
 * Get KYC profile for client
 */
export function getClientKYC(clientId: string): KYCProfile | null {
  return SEED_KYC_PROFILES[clientId] || null;
}

/**
 * Get client with KYC data
 */
export function getClientWithKYC(clientId: string): (ExtendedClient & { kyc_profile: KYCProfile }) | null {
  const client = getSeedClient(clientId);
  const kyc = getClientKYC(clientId);

  if (!client || !kyc) return null;

  return {
    ...client,
    kyc_profile: kyc
  };
}

/**
 * Simulate API delay
 */
export function simulateApiDelay(ms: number = 500): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

export default {
  SEED_CLIENTS,
  SEED_KYC_PROFILES,
  getSeedClient,
  getAllSeedClients,
  getClientKYC,
  getClientWithKYC,
  simulateApiDelay
};
